import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { catchError, map } from 'rxjs/operators';
import { DeleteData, FilterData, MessageResult, PostData, PutData } from 'src/app/demo/interfaces/crud.interface';

@Injectable({
  providedIn: 'root'
})
export class CrudService {
  urlBack: string = environment.URL_BACK;

  constructor(private httpClient: HttpClient) { }

  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que retorna un posible error al llamado de una peticion HTTP.
    * @param error Informacion del error
  */
  handleError(error: HttpErrorResponse): Observable<never> {
    const dataError = { status: error.status, message: error.message };
    return throwError(() => dataError);
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que retorna la informacion del modelo.
    * @param filter URL y filtros sobre el servicio.
  */
  getData(filter: FilterData): Observable<any> {
    let httpParams = new HttpParams();
    if (filter.pagination) {
      httpParams = httpParams.append('perPage', filter.pagination.rows)
        .append('page', filter.pagination.page + 1);
    }
    if (filter.filterForm) {
      for (const key in filter.filterForm) {
        if (filter.filterForm[key]) httpParams = httpParams.append(key, filter.filterForm[key]);
      }
    }
    return this.httpClient.get<any>(`${this.urlBack}${filter.url}`, { params: httpParams }).pipe(
      map(res => {
        const setStateRes = res;
        setStateRes.data = res.data.map((item: any) => {
          return {
            ...item,
            nameState: item.activo === 1 ? 'Activo' : 'Inactivo'
          };
        });
        return setStateRes;
      }),
      catchError(this.handleError)
    );
  }

  getDataArrayResponse(filter: FilterData): Observable<any[]> {
    let httpParams = new HttpParams();

    if (filter.pagination) {
      httpParams = httpParams
        .append('perPage', filter.pagination.rows)
        .append('page', filter.pagination.page + 1);
    }

    if (filter.filterForm) {
      for (const key in filter.filterForm) {
        if (filter.filterForm[key]) {
          httpParams = httpParams.append(key, filter.filterForm[key]);
        }
      }
    }

    return this.httpClient.get<any[]>(`${this.urlBack}${filter.url}`, { params: httpParams }).pipe(
      map((res: any[]) => {
        return res.map((item: any) => ({
          ...item,
          nameState: item.activo === 1 ? 'Activo' : 'Inactivo'
        }));
      }),
      catchError(this.handleError)
    );
  }

  getBasicData(filter: FilterData): Observable<any> {
    let httpParams = new HttpParams();

    if (filter.pagination) {
      httpParams = httpParams
        .append('perPage', filter.pagination.rows)
        .append('page', filter.pagination.page + 1);
    }

    if (filter.filterForm) {
      for (const key in filter.filterForm) {
        if (filter.filterForm[key]) {
          httpParams = httpParams.append(key, filter.filterForm[key]);
        }
      }
    }

    return this.httpClient.get<any>(`${this.urlBack}${filter.url}`, {
      params: httpParams
    }).pipe(
      catchError(this.handleError)
    );
  }


  getDataUser(filter: FilterData): Observable<{ fin: number, data_usuarios: any[], message: string }> {
    let httpParams = new HttpParams();

    if (filter.pagination) {
      httpParams = httpParams
        .append('perPage', filter.pagination.rows)
        .append('page', filter.pagination.page + 1);
    }

    if (filter.filterForm) {
      for (const key in filter.filterForm) {
        if (filter.filterForm[key]) {
          httpParams = httpParams.append(key, filter.filterForm[key]);
        }
      }
    }

    return this.httpClient
      .get<{ fin: number; data_usuarios: any[]; message: string }>(`${this.urlBack}${filter.url}`, { params: httpParams })
      .pipe(
        map(res => {
          // Si necesitás modificar los usuarios antes de retornarlos, lo podés hacer aquí
          res.data_usuarios = res.data_usuarios.map((usuario: any) => ({
            ...usuario,
            nameState: usuario.activo === 1 ? 'Activo' : 'Inactivo'
          }));
          return res;
        }),
        catchError(this.handleError)
      );
  }


  getIndicadorWithHojaVida(filter: FilterData): Observable<{
    indicador: any,
    hoja_vida: any,
    ultimos_reportes: any[]
  }> {
    let httpParams = new HttpParams();

    if (filter.pagination) {
      httpParams = httpParams
        .append('perPage', filter.pagination.rows)
        .append('page', filter.pagination.page + 1);
    }

    if (filter.filterForm) {
      for (const key in filter.filterForm) {
        if (filter.filterForm[key]) {
          httpParams = httpParams.append(key, filter.filterForm[key]);
        }
      }
    }

    return this.httpClient.get<{
      indicador: any,
      hoja_vida: any,
      ultimos_reportes: any[]
    }>(`${this.urlBack}${filter.url}`, { params: httpParams }).pipe(
      map((res) => {
        // Por ejemplo, transformar el estado del indicador:
        const indicador = {
          ...res.indicador,
          nameState: res.indicador?.activo === 1 ? 'Activo' : 'Inactivo'
        };

        // Puedes también normalizar valores de hoja_vida si quieres aquí
        return {
          indicador,
          hoja_vida: res.hoja_vida,
          ultimos_reportes: res.ultimos_reportes
        };
      }),
      catchError(this.handleError)
    );
  }


  /**
   * @author Ministerio de cultura
   * @createdate 2024-10-14
   * Metodo que retorna la informacion del modelo sin la estructura de paginacion.
   * @param filter URL y filtros sobre el servicio.
 */
  getDataWithoutPagination(filter: FilterData, responseType: 'json' | 'text' | 'blob' = 'json'): Observable<any> {
    let httpParams = new HttpParams();
    if (filter.filterForm) {
      for (const key in filter.filterForm) {
        if (filter.filterForm[key]) httpParams = httpParams.append(key, filter.filterForm[key]);
      }
    }
    return this.httpClient.get<any>(`${this.urlBack}${filter.url}`, { params: httpParams, responseType: responseType as 'json' }).pipe(
      catchError(this.handleError)
    );
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que ejecuta el post sobre el modelo.
    * @param postData Cuerpo de la peticion.
  */
  postData(postData: PostData, responseType: string = 'json'): Observable<MessageResult | any> {
    return this.httpClient.post<MessageResult | any>(`${this.urlBack}${postData.url}`, postData.formData.value || postData.formData, { responseType: responseType as 'json' }).pipe(
      catchError(this.handleError)
    );
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que ejecuta el put sobre el modelo.
    * @param postData Cuerpo de la peticion.
  */
  putData(putData: PutData): Observable<MessageResult> {
    const url = putData.id ? `${this.urlBack}${putData.url}/${putData.id}` : `${this.urlBack}${putData.url}`;
    return this.httpClient.put<MessageResult>(url, putData.formData.value || putData.formData).pipe(
      catchError(this.handleError)
    );
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que descarga un archivo del servidor.
    * @param url Url del servicio del back.
  */
  downloadFile(url: string): Observable<Blob> {
    return this.httpClient.get<Blob>(`${this.urlBack}${url}`, { responseType: 'blob' as 'json' }).pipe(
      catchError(this.handleError)
    );
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que genera un form data de un JSON.
    * @param data Informacion del formulario.
  */
  createFormData(data: any): FormData {
    const formData = new FormData();
    for (const key in data) {
      if (key !== 'archivo' && key !== 'archivos' && key !== 'file' && key !== 'url_evidencias') {
        if (data[key] !== null) {
          if (data[key] instanceof Date) formData.append(key, data[key].toISOString());
          else if (typeof data[key] !== 'string') formData.append(key, JSON.stringify(data[key]));
          else formData.append(key, data[key]);
        }
      } else {
        if (key === 'archivos') {
          data.archivos.forEach((file: File, index: number) => {
            formData.append(`archivos[${index}]`, file);
          });
        } else formData.append(key, data[key]);
      }
    }
    return formData;
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-26
    * Metodo que ejecuta el delete sobre el modelo.
    * @param deleteData Cuerpo de la peticion.
  */
  deleteData(deleteData: DeleteData): Observable<MessageResult> {
    return this.httpClient.delete<MessageResult>(`${this.urlBack}${deleteData.url}/${deleteData.id}`, { body: deleteData.formData }).pipe(
      catchError(this.handleError)
    );
  }
}